import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../auth/usePermissions';
import {
  changeStatus,
  extendProcessing,
  fetchLiveTranscript,
  fetchWorkplaceState,
  finishProcessing,
  soobshitOBezdeystvii,
  type LiveTranscriptPayload,
  type WorkplaceState,
} from './workplaceApi';

/**
 * Состояние своего рабочего места — живьём (ТЗ §4.5, §13.3).
 *
 * Один хук на два места сразу: сам кабинет и постоянный блок статуса в левом
 * баре, который виден на ЛЮБОЙ странице (§4.0). Общий кэш запроса делает их
 * одним источником: две независимые подписки показывали бы разный статус в
 * одном окне — а статус это вентиль обзвона, и «Готов» в баре при «Перерыве»
 * в кабинете означает звонки в пустоту.
 *
 * ⭐ Транспорт — короткий опрос, а не поток событий, хотя §13.3 называет SSE.
 *
 * Замерено на этом сервере: php-fpm держит ПЯТЬ рабочих процессов на всю
 * админку, а открытый поток занимает один целиком, пока живёт; браузер
 * переподключается сразу, то есть каждая открытая вкладка кабинета забирает
 * процесс насовсем. Три оператора — и админка встаёт для всех, включая
 * владельца на других страницах. Опрос раз в две секунды даёт ту же задержку,
 * что тик потока, и держит процесс миллисекунды вместо минуты.
 */
export type LiveTranscript = LiveTranscriptPayload;

export const WORKPLACE_STATE_KEY = ['telephony', 'workplace', 'state'] as const;
export const WORKPLACE_TRANSCRIPT_KEY = ['telephony', 'workplace', 'transcript'] as const;

/** Как часто спрашиваем состояние. Две секунды — тот же шаг, что был у потока. */
const POLL_MS = 2000;

export interface WorkplaceStateHook {
  state: WorkplaceState | null;
  transcript: LiveTranscript | null;
  /** Секунды в статусе, тикающие на экране между ответами сервера. */
  secondsInStatus: number;
  /** Сколько осталось до конца перерыва или обработки; null — срока нет. */
  secondsLeft: number | null;
  live: boolean;
  error: string | null;
  busy: boolean;
  setReady: () => Promise<void>;
  setBreak: (minutes: number) => Promise<void>;
  setAway: (awayKindId?: number) => Promise<void>;
  /** `s779`: продлить обработку на выбранные минуты; без числа — прежним интервалом. */
  extend: (minutes?: number) => Promise<void>;
  finish: () => Promise<void>;
  /**
   * `s779`: сказать станции, что человек в «Готов» бездействует и не откликнулся.
   * `s818`: сперва перечитать состояние — вкладка из фона судит по старому; `eshchyoTiho` решает по свежему.
   */
  idleAway: (eshchyoTiho: (svezhee: WorkplaceState | null) => boolean) => Promise<void>;
}

export function useWorkplaceState(enabled = true): WorkplaceStateHook {
  const { can, sectionHidden } = usePermissions();
  const mayRead = enabled && can('tel.workplace') && !sectionHidden('tel-workplace');
  const queryClient = useQueryClient();
  const [commandError, setCommandError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: WORKPLACE_STATE_KEY,
    queryFn: fetchWorkplaceState,
    enabled: mayRead,
    refetchInterval: POLL_MS,
    // Вкладка в фоне статус не спрашивает: смотреть на него некому, а процессы
    // сервера общие. Вернулись на вкладку — TanStack сам обновит.
    refetchIntervalInBackground: false,
    staleTime: POLL_MS,
    retry: false,
  });

  const state = mayRead ? query.data ?? null : null;
  const inCall = state?.status === 'on_call';
  const afterCall = state?.status === 'processing';

  const transcriptQuery = useQuery({
    queryKey: WORKPLACE_TRANSCRIPT_KEY,
    queryFn: fetchLiveTranscript,
    /*
     * ⭐ Лента спрашивается и ПОСЛЕ отбоя — пока идёт «Обработка».
     *
     * Раньше опрос обрывался вместе с разговором, и последние секунды текста,
     * которые сервер дожёвывает уже после конца звонка, на экран не попадали
     * никогда (жалоба владельца 13.08). А заполняют итог именно по концовке
     * разговора — по тому, о чём договорились.
     *
     * Вне разговора и обработки лента не спрашивается: на сервере нечего
     * распознавать, а каждый лишний запрос — работа ffmpeg и распознавания.
     */
    enabled: mayRead && (inCall || afterCall),
    // В обработке текст уже почти собран — реже шаг, меньше пустых запросов.
    refetchInterval: inCall ? POLL_MS : POLL_MS * 2,
    refetchIntervalInBackground: false,
    retry: false,
  });

  /*
   * Часы экрана. Тикают раз в секунду, но СЧИТАЮТСЯ не тики, а разница с
   * временем сервера: вкладка в фоне тикает реже, и таймер, собранный из
   * тиков, отстал бы на минуты — человек увидел бы «перерыв 3 минуты» на
   * двадцатой.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [enabled]);

  const secondsInStatus = state?.since
    ? Math.max(0, Math.floor((now - Date.parse(state.since)) / 1000))
    : (state?.seconds_in_status ?? 0);

  const secondsLeft = state?.planned_until
    ? Math.max(0, Math.floor((Date.parse(state.planned_until) - now) / 1000))
    : null;

  const run = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);

      try {
        await action();
        setCommandError(null);
      } catch (e: unknown) {
        /*
         * ⭐ «Завершать нечего: менеджер не в обработке» — не ошибка человека.
         * Обработку могла закрыть соседняя вкладка или станция (`s779`: без
         * ответа экрану — «Отошёл»), и нажатие через секунду после этого —
         * обычное дело. Владелец 12.08 получил это красной строкой и решил,
         * что сломал систему. Говорим, что произошло на самом деле.
         */
        const message = readError(e);

        setCommandError(
          /не в обработке/i.test(message)
            ? 'Обработка уже завершена.'
            : message,
        );
      } finally {
        // Перечитываем в любом случае: отказ сервера — тоже новость о
        // состоянии, и экран не должен остаться с тем, чего не случилось.
        await queryClient.invalidateQueries({ queryKey: WORKPLACE_STATE_KEY });
        setBusy(false);
      }
    },
    [queryClient],
  );

  return {
    state,
    transcript: mayRead ? transcriptQuery.data ?? null : null,
    secondsInStatus,
    secondsLeft,
    // «Связь есть» — это свежий ответ сервера, а не открытый сокет.
    live: mayRead && !query.isError && query.data !== undefined,
    /*
     * ⭐ Отказ «телефон не подключён» гаснет, как только линия появилась
     * (инцидент 13.08: на экране одновременно висели «Телефон подключён» и
     * красная строка «не подключён»). Сообщение об ошибке — не запись в
     * журнале, а подсказка о ТЕКУЩЕМ положении дел; пережив свою причину,
     * оно превращается в неправду.
     */
    error: !mayRead ? null : (state?.line_online && commandError?.includes('Телефон не подключён')
      ? null
      : commandError) ?? (query.error ? readError(query.error) : null),
    busy,
    setReady: () => run(() => changeStatus({ status: 'ready' })),
    setBreak: (minutes: number) => run(() => changeStatus({ status: 'break', minutes })),
    setAway: (awayKindId?: number) =>
      run(() => changeStatus({ status: 'away', ...(awayKindId ? { away_kind_id: awayKindId } : {}) })),
    extend: (minutes?: number) => run(() => extendProcessing(minutes)),
    finish: () => run(finishProcessing),
    idleAway: (eshchyoTiho: (svezhee: WorkplaceState | null) => boolean) => run(async () => {
      const svezhee = await queryClient.fetchQuery({ queryKey: WORKPLACE_STATE_KEY, queryFn: fetchWorkplaceState, staleTime: 0 });
      if (eshchyoTiho(svezhee)) await soobshitOBezdeystvii();
    }),
  };
}

function readError(e: unknown): string {
  const response = (e as { response?: { data?: { message?: string }; status?: number } }).response;

  if (response?.data?.message) return response.data.message;
  if (response?.status === 403) return 'Нет доступа к рабочему месту телефонии.';

  return 'Не получилось связаться с телефонией.';
}
